import { HStack } from '@sgme/ui';
import type { ColDef, GridApi, GridReadyEvent, GridState, RowClickedEvent, StateUpdatedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { formatDate } from 'date-fns';
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { selectWorkspaceBlotterColumnState } from '@/api/workspace/selectors.ts';
import { DetailsDrawer } from '@/App/Blotter/grids/accumulator/details/DetailsDrawer.tsx';
import { useColumnDefinitions } from '@/App/Blotter/grids/accumulator/useColumnDefinitions.tsx';
import type { BlotterGridComponent } from '@/App/Blotter/grids/BlotterGridComponent.tsx';
import { getRowId } from '@/App/Blotter/grids/getRowId.ts';
import { sidebarConfiguration } from '@/App/Blotter/grids/sidebarConfiguration.ts';
import { useGridEventsTrackers } from '@/App/Blotter/useGridEventsTrackers.ts';
import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { blotterSlice } from '@/store/blotter/slice.ts';
import type { BlotterAccumulatorEntry } from '@/store/blotter/types/BlotterAccumulatorEntry.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';

const defaultColDef = {
  minWidth: 100,
  filter: false,
  sortable: true,
  suppressHeaderMenuButton: true,
  //cellClass: 'text-end',
} as const satisfies ColDef<BlotterAccumulatorEntry>;

const initialState = {
  sort: { sortModel: [{ colId: 'tradeDate', sort: 'desc' }] },
} as const satisfies GridState;

export const BlotterAccumulatorGrid: BlotterGridComponent = ({ ref }) => {
  const dispatch = useAppDispatch();

  const track = useTrackEvent();
  const gridEventsTrackers = useGridEventsTrackers('accumulator');

  const [tradeIdForDrawer, setTradeIdForDrawer] = useState<string | undefined>(undefined);
  const gridApiRef = useRef<{ api: GridApi<BlotterAccumulatorEntry> | undefined }>({ api: undefined });

  const entries = useAppSelector(blotterSlice.selectors.getAccumulatorEntries);
  const initialColumnState = useAppSelector(selectWorkspaceBlotterColumnState('accumulatorTab')) ?? [];

  const columnDefs = useColumnDefinitions();

  const handleGridReady = useCallback(
    ({ api }: GridReadyEvent<BlotterAccumulatorEntry>) => {
      gridApiRef.current.api = api;
      api.applyColumnState({ state: initialColumnState, applyOrder: true });
    },
    [initialColumnState],
  );

  const handleStateUpdated = useCallback(({ api }: StateUpdatedEvent<BlotterAccumulatorEntry>) => {
    dispatch(blotterSlice.actions.saveAccumulatorGridColumnState(api.getColumnState()));
  }, []);

  const handleRowClicked = useCallback(({ api, data }: RowClickedEvent<BlotterAccumulatorEntry>) => {
    if (api.getFocusedCell()?.column.getColId() === 'actions') {
      return;
    }

    setTradeIdForDrawer((currentTradeIdForBlotter) => {
      if (data?.id !== currentTradeIdForBlotter) {
        track('Show accumulator detail', data?.id);
        return data?.id;
      }

      // when a trade is displayed in the details drawer:
      // clicking the trade row again will hide the drawer, it is a better ergonomy than having the drawer contents unchanged
      // and let the user mouse traverse the screen to click on a tiny button

      track('Hide accumulator detail');
      return undefined;
    });
  }, []);

  const handleDetailsDrawerClose = useCallback(() => {
    setTradeIdForDrawer(undefined);
    track('Hide accumulator detail');
  }, []);

  useImperativeHandle(ref, () => ({
    isExportEnabled: () => entries.length > 0,
    export: () => {
      const api = gridApiRef.current.api;
      if (!api) {
        return;
      }

      const columnKeys = api
        .getColumnState()
        .filter(({ colId, hide }) => !hide && colId !== 'actions')
        .map(({ colId }) => colId);

      api.exportDataAsExcel({
        columnKeys,
        fileName: `Export-SGMarketsFX-accumulator-${formatDate(new Date(), 'yyyyMMdd-HHmmss')}.xlsx`,
      });
    },
  }));

  useEffect(() => {
    gridApiRef.current.api?.applyColumnState({ state: initialColumnState });
  }, [initialColumnState]);

  return (
    <HStack className="h-100">
      <AgGridReact<BlotterAccumulatorEntry>
        className="ag-theme-sg-bootstrap-condensed flex-grow-1"
        theme="legacy"
        columnDefs={columnDefs}
        rowData={entries}
        getRowId={getRowId}
        onGridReady={handleGridReady}
        initialState={initialState}
        defaultColDef={defaultColDef}
        onRowClicked={handleRowClicked}
        onStateUpdated={handleStateUpdated}
        suppressCellFocus
        sideBar={sidebarConfiguration}
        rowClassRules={{
          'bg-heather': ({ data }: { data: BlotterAccumulatorEntry | undefined }) => data?.id === tradeIdForDrawer,
        }}
        onSortChanged={gridEventsTrackers.handleSortChanged}
        onFilterChanged={gridEventsTrackers.handleFilterChanged}
        onToolPanelVisibleChanged={gridEventsTrackers.handleToolPanelVisibleChange}
        onColumnVisible={gridEventsTrackers.handleColumnVisible}
        onColumnMoved={gridEventsTrackers.handleColumnMoved}
        onColumnResized={gridEventsTrackers.handleColumnResized}
      />
      {tradeIdForDrawer && (
        <DetailsDrawer className="flex-grow-0" tradeId={tradeIdForDrawer} onClose={handleDetailsDrawerClose} />
      )}
    </HStack>
  );
};
